import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import type {
	OrderCheckoutDTO,
	OrderCryptoDTO,
	OrderDirectDTO,
	OrderPendingDTO,
} from '@libs/dtos/order/order-request.dto';
import { ProviderBalanceService } from '@services/provider/provider-balance.service';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export interface CreatePendingOrderResponse {
	success: boolean;
	orderId: number;
	message: string;
	/** Nothing to pay (0 €): the order was created paid and sent, no payment instructions */
	paid?: boolean;
}

export type CryptoCurrency = OrderCryptoDTO['cryptoCurrency'];

export interface CreateCryptoOrderResponse {
	success: boolean;
	orderId: number;
	walletAddress: string;
	cryptoAmount: string;
	cryptoCurrency: string;
	exchangeRate: number;
	expiresAt: string;
	/** Nothing to pay (0 €): the order was created paid and sent, no payment instructions */
	paid?: boolean;
}

export interface CreateOrderCheckoutResponse {
	checkoutUrl: string;
}

export interface CreateOrderDirectResponse {
	success: boolean;
	orderId: number | null;
	message: string;
	/** Another order of the same service is running on the link: nothing was created */
	linkBusy?: boolean;
	blockingOrderId?: number;
}

export interface ProcessPaymentSuccessResponse {
	success: boolean;
	orderId?: number;
	message?: string;
	paymentStatus?: string;
	providerStatus?: string;
}

export interface OrderStatusResponse {
	orderId: string;
	status: string;
	paymentStatus: string;
}

@Injectable({
	providedIn: 'root',
})
export class PaymentsService {
	private readonly baseUrl = `${environment.pathBackend}/payments`;
	private readonly stripeBaseUrl = `${environment.pathBackend}/stripe`;

	private providerBalance = inject(ProviderBalanceService);

	constructor(private http: HttpClient) {}

	createOrderCheckout(data: OrderCheckoutDTO): Observable<CreateOrderCheckoutResponse> {
		return this.http.post<CreateOrderCheckoutResponse>(`${this.baseUrl}/checkout`, data);
	}

	createOrderDirect(data: OrderDirectDTO): Observable<CreateOrderDirectResponse> {
		return this.http
			.post<CreateOrderDirectResponse>(`${this.baseUrl}/direct`, data)
			.pipe(finalize(() => this.providerBalance.refresh()));
	}

	redirectToCheckout(checkoutUrl: string): void {
		window.location.href = checkoutUrl;
	}

	checkOrderStatus(orderId: string): Observable<OrderStatusResponse> {
		return this.http.get<OrderStatusResponse>(`${this.baseUrl}/${orderId}/status`);
	}

	processPaymentSuccess(orderId: string, sessionId: string): Observable<ProcessPaymentSuccessResponse> {
		return this.http.post<ProcessPaymentSuccessResponse>(`${this.baseUrl}/${orderId}/process-success`, {
			sessionId,
		});
	}

	createPendingOrder(data: OrderPendingDTO): Observable<CreatePendingOrderResponse> {
		return this.http.post<CreatePendingOrderResponse>(`${this.baseUrl}/pending`, data);
	}

	confirmPayment(orderId: number): Observable<{ success: boolean; message: string }> {
		return this.http
			.post<{ success: boolean; message: string }>(`${this.baseUrl}/${orderId}/confirm`, {})
			.pipe(finalize(() => this.providerBalance.refresh()));
	}

	createCryptoOrder(data: OrderCryptoDTO): Observable<CreateCryptoOrderResponse> {
		return this.http.post<CreateCryptoOrderResponse>(`${this.baseUrl}/crypto`, data);
	}
}
